import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { generatePath } from 'react-router-dom';
import { Response } from '@ui/src';
import { ClientRange, Turnover } from '../../../types';
import { corporateClientsRiskApi } from '../services';
import ROUTES from '../../../../routes';

export interface CorporateClientsRiskVolumesListState {
  values?: Turnover[];
  deleteInProgress: boolean;
  deleteIsSuccess?: boolean;
  selectedList: string[];
  activeState?: boolean;
}

const initialState: CorporateClientsRiskVolumesListState = {
  values: undefined,
  deleteInProgress: false,
  selectedList: [],
  activeState: true,
};

export const corporateClientsRiskVolumesListSlice = createSlice({
  name: 'corporateClientsRiskVolumesList',
  initialState,
  reducers: {
    setActiveState: (state, action: PayloadAction<boolean>) => {
      state.activeState = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      corporateClientsRiskApi.endpoints.getCorporateVolumes.matchFulfilled,
      (state, action: PayloadAction<Response<ClientRange>>) => {
        const monthlyTurnover = action.payload.data.edges
          .map((item) => item.node)
          .sort((a, b) => a.from - b.from);

        state.values = monthlyTurnover.map((item) => ({
          name: '',
          active: item.active,
          from: item.from,
          id: item.id,
          riskCategory: '-',
          riskScore: item.riskScore,
          type: item.type,
          url: generatePath(ROUTES.CORPORATE_CLIENTS_RISK_VOLUMES_EDIT, {
            itemId: item.id,
          }),
        }));
      },
    );
    builder.addMatcher(
      corporateClientsRiskApi.endpoints.createCorporateVolumes.matchFulfilled,
      (state, action: PayloadAction<{ data: ClientRange }>) => {
        let nextRanges: Turnover[] = [];

        if (state.values) {
          nextRanges = ([] as Turnover[]).concat(state.values);
        }

        nextRanges.push({
          name: '',
          active: action.payload.data.active,
          from: action.payload.data.from,
          id: action.payload.data.id,
          riskCategory: '-',
          riskScore: action.payload.data.riskScore,
          type: action.payload.data.type,
          url: generatePath(ROUTES.CORPORATE_CLIENTS_RISK_VOLUMES_EDIT, {
            itemId: action.payload.data.id,
          }),
        });

        state.values = nextRanges.sort((a, b) => a.from - b.from);
      },
    );
    builder.addMatcher(
      corporateClientsRiskApi.endpoints.updateCorporateVolumes.matchFulfilled,
      (state, action: PayloadAction<{ data: ClientRange }>) => {
        if (state.values) {
          state.values = state.values.map((item) => {
            let result = { ...item };

            if (item.id === action.payload.data.id) {
              result = {
                name: '',
                active: action.payload.data.active,
                from: action.payload.data.from,
                id: action.payload.data.id,
                riskCategory: '-',
                riskScore: action.payload.data.riskScore,
                type: action.payload.data.type,
                url: generatePath(ROUTES.CORPORATE_CLIENTS_RISK_VOLUMES_EDIT, {
                  itemId: action.payload.data.id,
                }),
              };
            }

            return result;
          });
        }
      },
    );
    builder.addMatcher(
      corporateClientsRiskApi.endpoints.batchDeleteCorporateVolumes
        .matchFulfilled,
      (state, action: any) => {
        if (state.values) {
          state.values = state.values.filter(
            (item) => item.id !== action.meta.arg.originalArgs,
          );
        }
      },
    );
  },
});

export const { setActiveState } = corporateClientsRiskVolumesListSlice.actions;

export default corporateClientsRiskVolumesListSlice.reducer;
